/**
 * URL guards for every place a server / third-party value becomes a navigation target or a media/link sink.
 * Scraped references, competitor ads, LLM output and task payloads are untrusted: never let them produce
 * `javascript:` / `data:` URLs or navigate the SPA to a foreign origin.
 */

function origin(): string {
  return typeof window !== "undefined" && window.location?.origin ? window.location.origin : "http://localhost";
}

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

/**
 * In-app navigation target (React Router `<Link to>` / `navigate`). Accepts same-origin absolute URLs and
 * root-relative paths ("/b/x/home?tab=1#a"); rejects other origins, non-http(s) schemes, protocol-relative
 * ("//evil.com") and bare relative paths. Returns `pathname + search + hash`, or null.
 */
export function safeInAppHref(href: unknown): string | null {
  if (typeof href !== "string") return null;
  const raw = href.trim();
  if (!raw || CONTROL.test(raw)) return null;
  const base = origin();
  let url: URL;
  if (raw.startsWith("/")) {
    if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
    try {
      url = new URL(raw, base);
    } catch {
      return null;
    }
  } else {
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      return null; // bare relative paths ("foo/bar") are not accepted
    }
    url = parsed;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (url.origin !== base) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Media / outbound link URL (img/video `src`, `poster`, `<a href>`, `window.open`): http(s) absolute URLs or
 * same-origin root-relative paths (e.g. signed `/media/<key>?exp=…&sig=…` — query strings are kept intact).
 * Anything else (javascript:, data:, blob: from the server, protocol-relative, garbage) → undefined.
 */
export function safeMediaHref(u: unknown): string | undefined {
  if (typeof u !== "string") return undefined;
  const raw = u.trim();
  if (!raw || CONTROL.test(raw)) return undefined;
  if (raw.startsWith("/")) {
    if (raw.startsWith("//") || raw.startsWith("/\\")) return undefined;
    try {
      const url = new URL(raw, origin());
      return url.origin === origin() ? raw : undefined;
    } catch {
      return undefined;
    }
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
  return raw; // unchanged (signed query strings stay byte-identical); control chars were rejected above
}

/** Locally created previews (`URL.createObjectURL`) plus everything `safeMediaHref` allows. */
export function safePreviewSrc(u: unknown): string | undefined {
  if (typeof u === "string" && u.startsWith("blob:")) {
    try {
      const url = new URL(u);
      // blob URLs carry the creating document's origin: blob:http://localhost:5173/uuid
      return url.protocol === "blob:" && u.slice(5).startsWith(origin()) ? u : undefined;
    } catch {
      return undefined;
    }
  }
  return safeMediaHref(u);
}
